import { useEffect, useState } from 'react';
import { useSignalrConnection } from 'hooks';
import { Button, DialogContent, DialogTitle, MenuItem, TextField, Avatar } from '@mui/material';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import { useAppDispatch } from 'store';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { useApiRequests } from 'hooks';
import { HubPatient } from 'types';
import { ModalTitle } from 'components';
import { StaffItem } from '../../../types/api';
import { useSnackbar } from 'notistack';
import api from 'api/api';

import styles from './TransferDialog.module.scss';

interface TransferDialogProps extends DialogProps {
  patient: HubPatient | null;
  onClose: () => void;
}

export function TransferDialog({ open, patient, onClose, ...rest }: TransferDialogProps) {
  const { getStaff } = useApiRequests();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { events } = useSignalrConnection(); // ✅ Use SignalR for real-time updates
  const currentUser = useAppSelector(selectUser);
  const currentClinicUserId = currentUser?.clinicUser?.id?.toLowerCase?.().trim();

  const [staffList, setStaffList] = useState<
    {
      id: string;
      name: string;
      preferedDisplayName?: string | null;
      roomName: string;
      avatarUrl: string;
    }[]
  >([]);
  const [selectedStaffId, setSelectedStaffId] = useState<string>('');

  const getDisplayName = (staff: { name: string; preferedDisplayName?: string | null }) =>
    staff.preferedDisplayName?.trim() || staff.name;

  useEffect(() => {
    if (open) {
      Promise.allSettled([
        getStaff({ count: 50, offset: 0 }),
        api.request<StaffItem[]>({ url: '/api/invitation/admins', method: 'get' }),
      ])
        .then((results) => {
          console.log('📡 Full API Response (Staff + Admins):', JSON.stringify(results, null, 2));

          // Fallback workaround: grab current roomName from visible UI
          const roomNameInput = document.querySelector<HTMLInputElement>('input.MuiInputBase-input');
          const currentRoomName = roomNameInput?.value?.trim().toLowerCase();
          console.log('🏠 Current visible roomName:', currentRoomName);

          const staffResponse =
            results[0].status === 'fulfilled' ? (results[0].value as { items: StaffItem[] }) : { items: [] };

          // 🛠️ Fix: adminResponse comes as raw array, not { data: [...] }
          const admins =
            results[1].status === 'fulfilled' && Array.isArray(results[1].value)
              ? (results[1].value as StaffItem[])
              : [];

          const staffOnly = staffResponse.items || [];

          // Step 1: Combine staff and admins, filtering out current room owner and users without roomName
          const combined: StaffItem[] = [...staffOnly, ...admins].filter((user) => {
            const room = typeof user.roomName === 'string' ? user.roomName.trim().toLowerCase() : '';
            const isRoomOwner = room && room === currentRoomName;
            const isSelf = user.clinicUserId?.toLowerCase?.() === currentClinicUserId;
            return !!user.clinicUserId && !!room && !isRoomOwner && !isSelf;
          });

          // Step 2: Deduplicate by clinicUserId
          const uniqueStaff: StaffItem[] = combined.reduce((acc: StaffItem[], user) => {
            if (!acc.some((existing) => existing.clinicUserId === user.clinicUserId)) {
              acc.push(user);
            }
            return acc;
          }, []);

          // Step 3: Inject fallback admin if not present
          const hasAdminAlready = admins.some(
            (admin) =>
              !!admin.clinicUserId &&
              !!admin.roomName &&
              !admin.roomName?.toLowerCase().includes(currentRoomName || '') &&
              uniqueStaff.some((u) => u.clinicUserId === admin.clinicUserId),
          );

          if (!hasAdminAlready) {
            const fallbackAdmin = admins.find(
              (admin) =>
                !!admin.clinicUserId &&
                !!admin.roomName &&
                !admin.roomName?.toLowerCase().includes(currentRoomName || ''),
            );

            if (fallbackAdmin) {
              uniqueStaff.push(fallbackAdmin);
            }
          }

          const staffList = uniqueStaff.map((user: StaffItem) => ({
            id: user.clinicUserId,
            name: user.name,
            preferedDisplayName: user.preferedDisplayName ?? null,
            roomName: user.roomName,
            avatarUrl: user.linkToProfilePicture ?? '',
          }));

          setStaffList(staffList);
        })
        .catch((err) => {
          console.error('❌ Error fetching staff or admins:', err);
          setStaffList([]);
        });
    }
  }, [open]);

  useEffect(() => {
    console.log('📡 Registering SignalR listener for provider status updates...');
  }, []);

  const handleTransfer = async () => {
    if (!patient || !selectedStaffId) {
      console.error('❌ Transfer failed: Missing patient or selectedStaffId');
      return;
    }

    const selectedStaff = staffList.find((s) => s.id === selectedStaffId);
    if (!selectedStaff) {
      console.warn(`❌ Staff ID ${selectedStaffId} not found in staffList`);
      return;
    }

    const transferURL = `${window.location.origin}/${selectedStaff.roomName}`;

    console.log('✅ Transfer Request Data:', {
      PatientConnectionId: patient.connectionId,
      NewRoomUrl: transferURL,
    });

    try {
      const response = await fetch(`${process.env['REACT_APP_HIPAA_LINK_API_URI']}api/transfer/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          PatientConnectionId: patient.connectionId,
          NewRoomUrl: transferURL,
        }),
      });

      console.log('✅ Transfer API Response:', response);

      if (response.ok) {
        console.log('✅ Transfer request sent successfully.');

        enqueueSnackbar('Client transferred successfully!', { variant: 'success' });

        onClose();
      } else {
        console.error('❌ Failed to transfer client:', await response.text());

        enqueueSnackbar('Failed to transfer client.', { variant: 'error' });
      }
    } catch (error) {
      console.error('❌ Error transferring client:', error);

      enqueueSnackbar('Transfer failed due to a network error.', { variant: 'error' });
    }
  };

  return (
    <Dialog {...rest} open={open} className={styles.dialog}>
      <DialogTitle>
        <ModalTitle title='Transfer To:' onClose={onClose} />
      </DialogTitle>
      <DialogContent className={styles.scrollableContent}>
        <TextField
          select
          value={selectedStaffId}
          onChange={(e) => setSelectedStaffId(e.target.value)}
          fullWidth
          variant='outlined'
          SelectProps={{
            displayEmpty: true,
            MenuProps: {
              PaperProps: {
                className: styles.customDropdown,
                sx: { mt: 1 },
                style: {
                  maxHeight: staffList.length > 5 ? 250 : 'auto',
                  overflowY: staffList.length > 5 ? 'auto' : 'hidden',
                },
              },
              disableAutoFocusItem: true, // Prevents auto-scrolling to selected item
            },
            renderValue: (selectedId) => {
              if (!selectedId) {
                return <span className={styles.placeholder}>- Select Staff -</span>;
              }

              const staff = staffList.find((s) => s.id === selectedId);
              if (!staff) {
                console.warn(`Staff ID ${selectedId} not found in staffList`);
                return 'Unknown';
              }

              return (
                <div className={styles.selectedStaff}>
                  <div className={styles.avatarWrapper}>
                    {staff.avatarUrl ? (
                      <Avatar
                        src={staff.avatarUrl}
                        className={styles.staffAvatar}
                        alt={staff.name}
                        imgProps={{
                          onError: (e) => (e.currentTarget.src = ''), // Fallback to initials if image fails to load
                        }}
                      />
                    ) : (
                      <Avatar className={styles.staffAvatar}>
                        {getDisplayName(staff)
                          .split(' ')
                          .slice(0, 2)
                          .map((n) => n.charAt(0).toUpperCase())
                          .join('')}
                      </Avatar>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
                    <strong className={styles.truncate}>{getDisplayName(staff)}</strong>
                    <span className={styles.selectedStaffroom}>room url: /{staff.roomName}</span>
                  </div>
                </div>
              );
            },
          }}
        >
          {staffList.length === 0 ? (
            <MenuItem disabled>
              <span className={styles.placeholder}>No Available Staff</span>
            </MenuItem>
          ) : (
            staffList.map((staff) => (
              <MenuItem key={staff.id} value={staff.id} className={styles.staffOption}>
                <div className={styles.avatarWrapper}>
                  {staff.avatarUrl ? (
                    <Avatar
                      src={staff.avatarUrl}
                      className={styles.staffAvatar}
                      alt={staff.name}
                      imgProps={{
                        onError: (e) => (e.currentTarget.src = ''), // Fallback to initials if image fails to load
                      }}
                    />
                  ) : (
                    <Avatar className={styles.staffAvatar}>
                      {getDisplayName(staff)
                        .split(' ')
                        .slice(0, 2)
                        .map((n) => n.charAt(0).toUpperCase())
                        .join('')}
                    </Avatar>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
                  <strong className={styles.truncate}>{getDisplayName(staff)}</strong>
                  <span className={styles.selectedStaffroom}>room url: /{staff.roomName}</span>
                </div>
              </MenuItem>
            ))
          )}
        </TextField>
        <div className={styles.buttonContainer}>
          <Button variant='outlined' fullWidth className={styles.cancelButton} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant='contained'
            fullWidth
            className={styles.transferButton}
            onClick={handleTransfer}
            disabled={!selectedStaffId}
          >
            Transfer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
